import type { TRowSort } from '@homepal/api-contract'

import { IUnderlyingDataResponse } from 'types/GlobalWidget'

import { Order, Sort } from 'utils/types'

import {
  ColumnSearch,
  ParsedData,
  filterParsedData,
  sortParsedData
} from 'utils/underlyingData'

import { TDataDialogColumn } from './types'

export const INITIAL_SORT: Sort = {
  attribute: 'segment',
  direction: 'asc' as Order
}

// Column selection is cached per source key (metric id / key metric id /
// selected KPI option id) so switching KPI keeps each view's columns.
export type TDataDialogColumns = {
  [sourceKey: string]: {
    [columnId: string]: TDataDialogColumn
  }
}

export type TDataDialogDataCache = {
  [sourceKey: string]: {
    raw: IUnderlyingDataResponse
    parsed: ParsedData
    search: ColumnSearch
    sort: Sort
    // the sort the rows are shown in: the requested one, except while a
    // sort the server runs is being fetched
    rowsSort: Sort
    // the server search and sort the rows were fetched with, see
    // getServerSearchKey and getServerSortKey
    serverSearchKey: string
    serverSortKey: string
    // the selection has more rows than were fetched
    capped: boolean
  }
}

// What a capped table fetches again while staying on screen.
export type TServerRefresh = 'search' | 'sort'

// Where the table's rows stand: loading or failing to load, shown, fetched
// again for a search or sort while shown, or shown after that failed.
export type TRowsStatus =
  | 'loading'
  | 'failed'
  | 'ready'
  | TServerRefresh
  | 'refresh_failed'

export type TSelectedParsedData = ParsedData & {
  search: ColumnSearch
  sort: Sort
  // Row count before column searches are applied.
  totalRows: number
}

export const EMPTY_PARSED_DATA: ParsedData = { columns: [], rows: [] }

export function isInitialSort(sort: Sort): boolean {
  return (
    sort.attribute === INITIAL_SORT.attribute
    && sort.direction === INITIAL_SORT.direction
  )
}

// The sort to ask the server for: none for the initial sort, which is the
// server's own order, or for a column the server can't sort by (the metric's
// value).
export function getServerSort(
  sort: Sort,
  raw: IUnderlyingDataResponse
): TRowSort | undefined {
  const isSortable
    = sort.attribute === INITIAL_SORT.attribute
    || raw.data.some((column) => column.entity_column_id === sort.attribute)

  return isInitialSort(sort) || !isSortable
    ? undefined
    : { column: sort.attribute, direction: sort.direction }
}

// Identifies the server sort a fetch asks for, none included.
export function getServerSortKey(serverSort: TRowSort | undefined): string {
  return JSON.stringify(serverSort ?? null)
}

// The searches worth sending to the server: the ones with text.
export function getServerSearch(
  search: ColumnSearch
): ColumnSearch | undefined {
  const active = Object.entries(search).filter(
    ([, query]) => query.trim().length > 0
  )

  return active.length > 0 ? Object.fromEntries(active) : undefined
}

// Identifies the server search a fetch asks for, none included.
export function getServerSearchKey(
  serverSearch: ColumnSearch | undefined
): string {
  return JSON.stringify(serverSearch ?? null)
}

/**
 * Sorting and searching are split so a keystroke costs one filter pass.
 * Filtering a sorted list keeps its order, so the sort only has to re-run
 * when the data or the sort itself changes. A capped table whose rows the
 * server sorted keeps the server's order, which decided the rows it holds.
 */
export function getSortedParsedData(
  entry: TDataDialogDataCache[string] | undefined
): ParsedData {
  if (!entry) {
    return EMPTY_PARSED_DATA
  }

  const serverSort = getServerSort(entry.rowsSort, entry.raw)
  const isServerOrder
    = entry.capped
    && getServerSortKey(serverSort) === entry.serverSortKey
    && (!serverSort ? isInitialSort(entry.rowsSort) : !!entry.raw.sorted)

  return isServerOrder
    ? entry.parsed
    : sortParsedData(entry.parsed, entry.rowsSort)
}

// The rows to show: the sorted set with the column searches applied.
export function getSearchedParsedData(
  sorted: ParsedData,
  search: ColumnSearch = {},
  sort: Sort = INITIAL_SORT
): TSelectedParsedData {
  return {
    ...filterParsedData(sorted, search),
    search,
    sort,
    totalRows: sorted.rows.length
  }
}

// Drop searches on columns that are no longer part of the visible selection.
export function pruneColumnSearch(
  search: ColumnSearch,
  visibleColumnIds: string[]
): ColumnSearch {
  const next: ColumnSearch = {}

  Object.entries(search).forEach(([columnId, query]) => {
    const isSegment = columnId === INITIAL_SORT.attribute

    if (isSegment || visibleColumnIds.includes(columnId)) {
      next[columnId] = query
    }
  })

  return next
}
